import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { 
  ArrowLeft, Building2, Phone, Mail, MapPin, Plus, Edit, 
  User, PieChart, Unlink, AlertTriangle, Shield, Eye, EyeOff, Save, Lock,
  FileSignature, MoreHorizontal
} from 'lucide-react';
import { toast } from 'sonner';
import ContactForm from '../components/contacts/ContactForm';
import ContactDocumentsTab from '../components/contacts/ContactDocumentsTab';
import ContactTimelineTab from '../components/contacts/ContactTimelineTab';
import OwnersSection, { PersonBusinesses } from '../components/contacts/OwnersSection';
import SendForSignatureDialog from '../components/deal-detail/SendForSignatureDialog';
import { formatCurrency } from '../lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatPhoneDisplay = (value) => {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return national.length === 10
    ? `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`
    : value;
};

const yearsInBusiness = (value) => {
  if (!value) return null;
  const started = new Date(value);
  if (Number.isNaN(started.getTime())) return null;
  return Math.max(0, Math.floor(((Date.now() - started.getTime()) / (365.25 * 24 * 3600 * 1000)) * 10) / 10);
};

const initialFormData = {
  company_name: '',
  shortcode: '',
  dba: '',
  industry: '',
  name: '',
  phones: [{ phone: '', type: 'Main' }],
  emails: [{ email: '', type: 'Business' }],
  website: [],
  source_company: '',
  source_contact: '',
  campaign: '',
  additional_info: '',
  business_description: '',
  third_party_id: '',
  addresses: [{ type: 'Main', street1: '', street2: '', city: '', state: '', zip: '', country: 'United States' }],
  is_funder: false,
  is_broker: false,
  is_merchant: false,
  is_iso: false,
  is_lawyer: false,
  is_collector: false,
  is_credit_card_processor: false,
  is_vendor: false,
  is_investor: false,
  is_partner: false,
  is_general: false,
  is_other: false,
  access_permission: 'Everyone',
  broker_commission_rate: '',
  owner_name: '',
  owner_ssn: '',
  owner_dob: '',
  owner_address: '',
  owner_city: '',
  owner_state: '',
  owner_zip: '',
  phone: '',
  email: '',
  business_address: '',
  business_city: '',
  business_state: '',
  business_zip: '',
  business_street2: '',
  ein: '',
  state_of_incorporation: '',
  business_start_date: '',
  is_person: false,
  dob: '',
  home_address: { street1: '', street2: '', city: '', state: '', zip: '' },
  collects_fees: false
};

const getStatusBadgeColor = (status) => {
  const colors = {
    'Funded': 'bg-green-100 text-green-800',
    'Payment Issues': 'bg-yellow-100 text-yellow-800',
    'In-House Collection': 'bg-orange-100 text-orange-800',
    'Default': 'bg-red-100 text-red-800',
    'Defaulted': 'bg-red-100 text-red-800',
    'Collected': 'bg-blue-100 text-blue-800',
    'Paid in Full': 'bg-purple-100 text-purple-800',
    'Paid in Full (Refinanced)': 'bg-indigo-100 text-indigo-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

const ContactProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const [contact, setContact] = useState(null);
  const [deals, setDeals] = useState([]);
  const [syndications, setSyndications] = useState([]);
  const [syndicationTotals, setSyndicationTotals] = useState({ total_invested: 0, total_available: 0, total_withdrawn: 0 });
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [signportDialogOpen, setSignportDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  
  // Unlink dialog state
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);
  const [dealToUnlink, setDealToUnlink] = useState(null);
  const [unlinking, setUnlinking] = useState(false);
  
  // Sensitive data state
  const [sensitiveData, setSensitiveData] = useState(null);
  const [sensitiveLoading, setSensitiveLoading] = useState(false);
  // Phase B (Josh): sensitive values are MASKED by default; explicit reveal only.
  const [showSensitive, setShowSensitive] = useState(false);
  const [showBankFull, setShowBankFull] = useState(false);
  const [sensitiveEditDialogOpen, setSensitiveEditDialogOpen] = useState(false);
  const [sensitiveFormData, setSensitiveFormData] = useState({
    ssn_full: '',
    account_holder: '',
    bank_name: '',
    bank_account_full: '',
    routing_number_full: '',
    acct_type: ''
  });
  const [savingSensitive, setSavingSensitive] = useState(false);
  const [migratingLegacySsn, setMigratingLegacySsn] = useState(false);
  
  const [metrics, setMetrics] = useState({
    totalDeals: 0,
    totalFunded: 0,
    totalPayback: 0,
    totalPaid: 0,
    totalBalance: 0,
    avgPaymentPercentage: 0,
    activeDeals: 0,
    completedDeals: 0,
    defaultedDeals: 0
  });

  const isAdmin = user?.roles?.includes('admin');
  const isPerson = contact?.is_person === true;
  const isSyndicator = contact?.is_investor || contact?.is_funder || contact?.is_partner;
  const primaryContactName = contact?.name || (!contact?.owners?.length ? contact?.owner_name : '');

  // 08/10 (Josh): one owner, multiple businesses — sibling companies linked to
  // this profile; their deals appear here too and their chips cross-navigate.
  const [linkedCompanies, setLinkedCompanies] = useState([]);
  const [addCoDlg, setAddCoDlg] = useState(null); // {name, shortcode, dba, saving}

  const fetchContactProfile = useCallback(async () => {
    try {
      const [contactRes, dealsRes] = await Promise.all([
        axios.get(`${API}/contacts/${id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/contacts/${id}/deals`, { headers: getAuthHeaders() })
      ]);

      setContact(contactRes.data);
      setDeals(dealsRes.data);
      calculateMetrics(dealsRes.data);

      try {
        const linkedRes = await axios.get(`${API}/contacts/${id}/linked-companies`, { headers: getAuthHeaders() });
        setLinkedCompanies(linkedRes.data.linked_companies || []);
      } catch (err) {
        console.error('Failed to fetch linked companies:', err);
        setLinkedCompanies([]);
      }
      
      // Fetch syndication data if contact is investor/funder
      if (contactRes.data.is_investor || contactRes.data.is_funder) {
        try {
          const syndRes = await axios.get(`${API}/contacts/${id}/syndications`, { headers: getAuthHeaders() });
          setSyndications(syndRes.data.syndications || []);
          setSyndicationTotals(syndRes.data.totals || { total_invested: 0, total_available: 0, total_withdrawn: 0 });
        } catch (err) {
          console.error('Failed to fetch syndications:', err);
        }
      }
      
      // Fetch financial/sensitive data for merchants (all authenticated users can see bank info)
      if (contactRes.data.is_merchant || contactRes.data.is_person) {
        setSensitiveLoading(true);
        try {
          const sensitiveRes = await axios.get(`${API}/contacts/${id}/sensitive`, { headers: getAuthHeaders() });
          setSensitiveData(sensitiveRes.data);
        } catch (err) {
          console.error('Failed to fetch sensitive data:', err);
        } finally {
          setSensitiveLoading(false);
        }
      }
    } catch (error) {
      console.error('Failed to fetch contact profile:', error);
      toast.error('Failed to load contact profile');
    } finally {
      setLoading(false);
    }
  }, [id, getAuthHeaders]);

  const fetchSensitiveData = useCallback(async () => {
    setSensitiveLoading(true);
    try {
      const res = await axios.get(`${API}/contacts/${id}/sensitive`, { headers: getAuthHeaders() });
      setSensitiveData(res.data);
    } catch (error) {
      console.error('Failed to fetch sensitive data:', error);
      // Don't show error toast - it's okay if no sensitive data exists
    } finally {
      setSensitiveLoading(false);
    }
  }, [id, getAuthHeaders]);

  useEffect(() => {
    fetchContactProfile();
  }, [fetchContactProfile]);

  const calculateMetrics = (dealsData) => {
    const totalDeals = dealsData.length;
    const totalFunded = dealsData.reduce((sum, d) => sum + (d.funding_amount || 0), 0);
    const totalPayback = dealsData.reduce((sum, d) => sum + (d.payback || 0), 0);
    const totalPaid = dealsData.reduce((sum, d) => sum + (d.payments_made || 0), 0);
    const totalBalance = dealsData.reduce((sum, d) => sum + (d.balance || 0), 0);
    const avgPaymentPercentage = totalPayback > 0 ? (totalPaid / totalPayback * 100) : 0;

    const activeDeals = dealsData.filter(d => d.status === 'Funded').length;
    const completedDeals = dealsData.filter(d => d.status === 'Paid in Full').length;
    const defaultedDeals = dealsData.filter(d => d.status === 'Default').length;

    setMetrics({
      totalDeals,
      totalFunded,
      totalPayback,
      totalPaid,
      totalBalance,
      avgPaymentPercentage,
      activeDeals,
      completedDeals,
      defaultedDeals
    });
  };

  const handleOpenEditDialog = () => {
    if (!contact) return;
    
    let phonesData = [{ phone: '', type: 'Main' }];
    if (contact.phones?.length > 0) {
      phonesData = contact.phones;
    } else if (contact.phone) {
      phonesData = [{ phone: contact.phone, type: 'Main' }];
    }
    
    let emailsData = [{ email: '', type: 'Business' }];
    if (contact.emails?.length > 0) {
      emailsData = contact.emails;
    } else if (contact.email) {
      emailsData = [{ email: contact.email, type: 'Business' }];
    }
    
    setFormData({
      ...initialFormData,
      ...contact,
      is_funder: contact.is_funder === true,
      is_broker: contact.is_broker === true,
      is_merchant: contact.is_merchant === true,
      is_iso: contact.is_iso === true,
      is_lawyer: contact.is_lawyer === true,
      is_collector: contact.is_collector === true,
      is_credit_card_processor: contact.is_credit_card_processor === true,
      is_vendor: contact.is_vendor === true,
      is_investor: contact.is_investor === true,
      is_general: contact.is_general === true,
      is_other: contact.is_other === true,
      collects_fees: contact.collects_fees === true,
      phones: phonesData,
      emails: emailsData,
      addresses: contact.addresses?.length > 0 ? contact.addresses : initialFormData.addresses,
      website: contact.website || [],
      owner_name: contact.owner_name || '',
      owner_ssn: contact.owner_ssn || '',
      owner_dob: contact.owner_dob?.split('T')[0] || '',
      owner_address: contact.owner_address || '',
      owner_city: contact.owner_city || '',
      owner_state: contact.owner_state || '',
      owner_zip: contact.owner_zip || '',
      phone: contact.phone || '',
      email: contact.email || '',
      business_address: contact.business_address || '',
      business_city: contact.business_city || '',
      business_state: contact.business_state || '',
      business_zip: contact.business_zip || '',
      business_street2: contact.business_street2 || '',
      ein: contact.ein || '',
      state_of_incorporation: contact.state_of_incorporation || '',
      business_start_date: contact.business_start_date?.split('T')[0] || '',
      is_person: contact.is_person === true,
      dob: contact.dob?.split('T')[0] || '',
      home_address: { ...initialFormData.home_address, ...(contact.home_address || {}) },
      business_description: contact.business_description || '',
      broker_commission_rate: contact.broker_commission_rate || ''
    });
    setEditDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const toNumberOrNull = (val) => {
      if (val === '' || val === null || val === undefined) return null;
      const num = parseFloat(val);
      return isNaN(num) ? null : num;
    };
    
    const cleanedData = {
      ...formData,
      phone: formData.phone || '',
      email: formData.email || '',
      addresses: formData.addresses.filter(a => a.street1 || a.city),
      dob: formData.dob || null,
      business_start_date: formData.business_start_date || null,
      broker_commission_rate: toNumberOrNull(formData.broker_commission_rate),
      referrer_contact_id: formData.is_iso ? (formData.referrer_contact_id || null) : null,   // 10/19: ISO's referrer
      referral_split_pct: formData.is_referrer ? toNumberOrNull(formData.referral_split_pct) : null   // 10/20: referrer's share of the 2% pool
    };

    try {
      await axios.put(`${API}/contacts/${id}`, cleanedData, { headers: getAuthHeaders() });
      toast.success('Contact updated successfully');
      setEditDialogOpen(false);
      fetchContactProfile();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to update contact';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      
      toast.error(errorMessage);
    }
  };

  const handleCreateDeal = () => {
    // 10/08 (Josh): the New Deal wizard reads `prefillDealWizard` (DealWizard.js);
    // `preFillDeal` below only ever fed the legacy form state, so the merchant
    // was never actually prefilled. Write both: the wizard gets the link.
    sessionStorage.setItem('prefillDealWizard', JSON.stringify({
      company_id: contact.id,
      deal_name: contact.company_name,
      deal_shortcode: contact.shortcode,
    }));
    sessionStorage.setItem('preFillDeal', JSON.stringify({
      company_id: contact.id,
      company_name: contact.company_name,
      shortcode: contact.shortcode,
      dba: contact.dba,
      industry: contact.industry,
      name: contact.name,
      phone: contact.phone || contact.phones?.[0]?.phone || '',
      email: contact.email || contact.emails?.[0]?.email || '',
      business_address: contact.business_address || '',
      owner_name: contact.owner_name || '',
      ein: contact.ein || '',
      monthly_revenue: contact.monthly_revenue || ''
    }));
    navigate('/deals?createNew=true');
  };

  // Unlink deal handlers
  const handleUnlinkClick = (e, deal) => {
    e.stopPropagation(); // Prevent row click navigation
    setDealToUnlink(deal);
    setUnlinkDialogOpen(true);
  };

  const handleConfirmUnlink = async () => {
    if (!dealToUnlink) return;
    
    setUnlinking(true);
    try {
      await axios.post(
        `${API}/contacts/${id}/unlink-deal/${dealToUnlink.id}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      toast.success(`Deal ${dealToUnlink.deal_id} unlinked from ${contact.company_name}`);
      setUnlinkDialogOpen(false);
      setDealToUnlink(null);
      
      // Refresh the deals list
      fetchContactProfile();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to unlink deal');
    } finally {
      setUnlinking(false);
    }
  };

  // Sensitive data handlers
  const handleOpenSensitiveEdit = () => {
    setSensitiveFormData({
      ssn_full: sensitiveData?.ssn_full || '',
      account_holder: sensitiveData?.account_holder || '',
      bank_name: sensitiveData?.bank_name || '',
      bank_account_full: sensitiveData?.bank_account_full || '',
      routing_number_full: sensitiveData?.routing_number_full || '',
      acct_type: sensitiveData?.acct_type || ''
    });
    setSensitiveEditDialogOpen(true);
  };

  const handleSaveSensitiveData = async (e) => {
    e.preventDefault();
    setSavingSensitive(true);
    
    try {
      // Only send non-empty fields
      const dataToSend = {};
      if (sensitiveFormData.ssn_full) dataToSend.ssn_full = sensitiveFormData.ssn_full;
      if (sensitiveFormData.account_holder) dataToSend.account_holder = sensitiveFormData.account_holder;
      if (sensitiveFormData.bank_name) dataToSend.bank_name = sensitiveFormData.bank_name;
      if (sensitiveFormData.bank_account_full) dataToSend.bank_account_full = sensitiveFormData.bank_account_full;
      if (sensitiveFormData.routing_number_full) dataToSend.routing_number_full = sensitiveFormData.routing_number_full;
      if (sensitiveFormData.acct_type) dataToSend.acct_type = sensitiveFormData.acct_type;
      
      await axios.patch(`${API}/contacts/${id}/sensitive`, dataToSend, { headers: getAuthHeaders() });
      toast.success('Sensitive data updated successfully');
      setSensitiveEditDialogOpen(false);
      await fetchSensitiveData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update sensitive data');
    } finally {
      setSavingSensitive(false);
    }
  };

  const handleMigrateLegacySsn = async () => {
    setMigratingLegacySsn(true);
    try {
      await axios.post(`${API}/contacts/${id}/sensitive/migrate-legacy-ssn`, {}, { headers: getAuthHeaders() });
      toast.success('Existing SSN moved to protected storage');
      await fetchSensitiveData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to move SSN to protected storage');
      await fetchSensitiveData();
    } finally {
      setMigratingLegacySsn(false);
    }
  };

  // Determine the role/association type for a deal (linked-company aware)
  const getDealRole = (deal) => {
    if (deal.company_id === id) return { role: 'Merchant', type: 'company_id' };
    if (deal.broker_id === id) return { role: 'Broker', type: 'broker_id' };
    if (deal.funder_id === id) return { role: 'Funder', type: 'funder_id' };
    const linkedIds = new Set(linkedCompanies.map((c) => c.id));
    if (linkedIds.has(deal.company_id)) return { role: 'Merchant (linked co.)', type: 'company_id' };
    if (linkedIds.has(deal.broker_id)) return { role: 'Broker (linked co.)', type: 'broker_id' };
    if (linkedIds.has(deal.funder_id)) return { role: 'Funder (linked co.)', type: 'funder_id' };
    return { role: 'Unknown', type: null };
  };

  // 08/10 (Josh): create ANOTHER company for the same owner, pre-linked.
  const submitAddCompany = async () => {
    if (!addCoDlg?.name?.trim() || !addCoDlg?.shortcode?.trim()) {
      toast.error('Company name and shortcode are required');
      return;
    }
    setAddCoDlg((d) => ({ ...d, saving: true }));
    try {
      const res = await axios.post(
        `${API}/contacts/${id}/add-company`,
        {
          company_name: addCoDlg.name.trim(),
          shortcode: addCoDlg.shortcode.trim().toUpperCase(),
          dba: addCoDlg.dba?.trim() || null,
        },
        { headers: getAuthHeaders() }
      );
      toast.success(`Created ${res.data.company_name} (${res.data.shortcode}) — linked to this profile`);
      setAddCoDlg(null);
      navigate(`/contacts/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create company');
      setAddCoDlg((d) => (d ? { ...d, saving: false } : d));
    }
  };

  const unlinkCompany = async (otherId, name) => {
    if (!window.confirm(`Unlink ${name} from this profile?`)) return;
    try {
      await axios.post(`${API}/contacts/${id}/unlink-company/${otherId}`, {}, { headers: getAuthHeaders() });
      toast.success(`Unlinked ${name}`);
      fetchContactProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlink');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  if (!contact) {
    return (
      <Layout>
        <div className="text-center py-12">
          {/* 08/05: a failed fetch (e.g. backend restarting mid-deploy) used to
              read "Contact not found" — misleading. Offer a retry. */}
          <p className="text-gray-500">Couldn't load this contact — it may exist but the server didn't respond.</p>
          <div className="mt-4 flex justify-center gap-2">
            <Button onClick={() => { setLoading(true); fetchContactProfile(); }}>
              Retry
            </Button>
            <Button variant="outline" onClick={() => navigate('/contacts')}>
              Back to Address Book
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="px-4 sm:px-0" data-testid="contact-profile-page">
        {/* Back Button & Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/contacts')}
            className="mb-4"
            data-testid="back-to-contacts"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Address Book
          </Button>

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{contact.company_name}</h1>
              {primaryContactName && !isPerson && (
                <p className="text-lg text-gray-600 flex items-center gap-2 mt-1">
                  <User className="h-4 w-4" />
                  <span><span className="font-medium">Primary Contact:</span> {primaryContactName}</span>
                </p>
              )}
              {contact.dba && (
                <p className="text-sm text-gray-500">DBA: {contact.dba}</p>
              )}
              <div className="flex flex-wrap gap-2 mt-2 items-center">
                {contact.contact_types?.map((type) => (
                  <Badge key={type} variant="outline">{type}</Badge>
                ))}
                {/* Default-for chips (Feb 2026 — DC ship). Renders one chip per type in is_default_for. */}
                {((contact.is_default_for || []).length > 0 || contact.is_default_lawyer) && (
                  <>
                    {[...new Set([
                      ...(contact.is_default_for || []),
                      ...(contact.is_default_lawyer ? ['lawyer'] : []),
                    ])].map(t => (
                      <Badge
                        key={t}
                        className="bg-amber-100 text-amber-800 border-amber-300"
                        data-testid={`default-for-${t}-badge`}
                      >
                        Default {t.charAt(0).toUpperCase() + t.slice(1)}
                      </Badge>
                    ))}
                  </>
                )}
              </div>
              {/* 08/10 (Josh): other businesses owned by the same person */}
              {linkedCompanies.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="linked-companies-row">
                  <span className="text-xs text-gray-500 uppercase tracking-wide">Other businesses:</span>
                  {linkedCompanies.map((c) => (
                    <span
                      key={c.id}
                      className="inline-flex items-center gap-1 border border-blue-200 bg-blue-50 rounded px-2 py-0.5 text-sm text-blue-800"
                    >
                      <button
                        onClick={() => navigate(`/contacts/${c.id}`)}
                        className="hover:underline"
                        data-testid={`linked-company-${c.shortcode}`}
                        title={`${c.deal_count} deal(s)`}
                      >
                        {c.company_name} <span className="font-mono text-xs">({c.shortcode})</span>
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => unlinkCompany(c.id, c.company_name)}
                          className="text-blue-400 hover:text-red-600"
                          aria-label={`Unlink ${c.company_name}`}
                        >
                          ×
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              {isAdmin && (
                <Button
                  variant="outline"
                  onClick={handleOpenEditDialog}
                  data-testid="edit-contact-btn"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  {isPerson ? 'Edit Person' : 'Edit Business'}
                </Button>
              )}
              {/* 10/08 (Josh): "Create New Deal" opens the New Deal wizard with this
                  merchant prefilled; sending a contract is its own button. */}
              {contact.is_merchant && (
                <Button
                  onClick={handleCreateDeal}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="create-deal-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Deal
                </Button>
              )}
              {contact.is_merchant && (
                <Button
                  variant="outline"
                  onClick={() => setSignportDialogOpen(true)}
                  data-testid="create-contract-btn"
                >
                  <FileSignature className="h-4 w-4 mr-2" />
                  Create New Contract
                </Button>
              )}
              {isAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" aria-label="More actions" data-testid="contact-more-actions">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {contact.is_merchant && <DropdownMenuItem onClick={handleCreateDeal} data-testid="record-funded-deal-btn"><Plus className="h-4 w-4 mr-2" /> Record Funded Deal</DropdownMenuItem>}
                    {!isPerson && <DropdownMenuItem onClick={() => setAddCoDlg({ name: '', shortcode: '', dba: '', saving: false })}><Building2 className="h-4 w-4 mr-2" /> Add Company</DropdownMenuItem>}
                    {!isPerson && <DropdownMenuSeparator />}
                    <DropdownMenuItem onClick={() => navigate('/contacts/merge')} data-testid="combine-contacts-btn">
                      Merge Records
                    </DropdownMenuItem>
                    {[
                      { key: 'lawyer', flag: 'is_lawyer', label: 'Set as Default Lawyer' },
                      { key: 'broker', flag: 'is_broker', label: 'Set as Default Broker' },
                      { key: 'merchant', flag: 'is_merchant', label: 'Set as Default Merchant' },
                      { key: 'funder', flag: 'is_funder', label: 'Set as Default Funder' },
                    ].map(({ key, flag, label }) => {
                      const alreadyDefault = (contact.is_default_for || []).includes(key) || (key === 'lawyer' && contact.is_default_lawyer);
                      if (!contact[flag] || alreadyDefault) return null;
                      return (
                        <DropdownMenuItem key={key} onClick={async () => {
                          try {
                            await axios.post(`${API}/contacts/${contact.id}/set-default-for/${key}`, {}, { headers: getAuthHeaders() });
                            toast.success(`${contact.company_name || contact.name} is now the default ${key}`);
                            fetchContactProfile();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || `Failed to set default ${key}`);
                          }
                        }} data-testid={`set-default-${key}-btn`}>
                          {label}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>

        {/* KPI Cards (deal metrics — business records only) */}
        {!isPerson && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Deals</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.totalDeals}</div>
              <p className="text-xs text-gray-500">
                {metrics.activeDeals} active, {metrics.completedDeals} completed
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Funded</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {formatCurrency(metrics.totalFunded)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Paid</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(metrics.totalPaid)}
              </div>
              <p className="text-xs text-gray-500">
                {metrics.avgPaymentPercentage.toFixed(1)}% of expected
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Balance Due</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {formatCurrency(metrics.totalBalance)}
              </div>
            </CardContent>
          </Card>
        </div>
        )}

        {/* Syndication Summary (only for investors/funders) */}
        {isSyndicator && (
          <Card className="mb-6 border-indigo-200 bg-indigo-50/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="h-5 w-5 text-indigo-600" />
                Syndication Summary
              </CardTitle>
              <CardDescription>Investment positions across all deals</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-white rounded-lg border">
                  <p className="text-sm text-gray-500">Total Invested</p>
                  <p className="text-2xl font-bold text-indigo-600">{formatCurrency(syndicationTotals.total_invested)}</p>
                </div>
                <div className="p-4 bg-white rounded-lg border">
                  <p className="text-sm text-gray-500">Available to Withdraw</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(syndicationTotals.total_available)}</p>
                </div>
                <div className="p-4 bg-white rounded-lg border">
                  <p className="text-sm text-gray-500">Total Withdrawn</p>
                  <p className="text-2xl font-bold text-gray-600">{formatCurrency(syndicationTotals.total_withdrawn)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="deals">Deals ({deals.length})</TabsTrigger>
            {isSyndicator && syndications.length > 0 && (
              <TabsTrigger value="syndications">Syndications ({syndications.length})</TabsTrigger>
            )}
            {isSyndicator && syndications.length > 0 && (
              <TabsTrigger value="capital-account">Capital Account</TabsTrigger>
            )}
            <TabsTrigger value="documents" data-testid="contact-docs-tab">Documents</TabsTrigger>
            <TabsTrigger value="timeline" data-testid="contact-timeline-tab">Timeline</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            {/* Person layout: identity + linked businesses */}
            {isPerson && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card data-testid="person-identity-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      Person Details
                    </CardTitle>
                    <CardDescription>Canonical identity — shared by every business this person owns or guarantees</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {contact.shortcode && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">Shortcode</p>
                        <p className="font-mono text-blue-600">{contact.shortcode}</p>
                      </div>
                    )}
                    <div>
                      <div className="flex items-center text-sm font-medium text-gray-500 mb-1"><Phone className="h-4 w-4 mr-2" />Cell Phone</div>
                      <p>{contact.phone || <span className="text-gray-400 italic">Not provided</span>}</p>
                    </div>
                    <div>
                      <div className="flex items-center text-sm font-medium text-gray-500 mb-1"><Mail className="h-4 w-4 mr-2" />Personal Email</div>
                      <p>{contact.email || <span className="text-gray-400 italic">Not provided</span>}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Date of Birth</p>
                      <p>{contact.dob?.split('T')[0] || <span className="text-gray-400 italic">Not provided</span>}</p>
                    </div>
                    <div>
                      <div className="flex items-center text-sm font-medium text-gray-500 mb-1"><MapPin className="h-4 w-4 mr-2" />Home Address</div>
                      {contact.home_address && (contact.home_address.street1 || contact.home_address.city) ? (
                        <div className="text-sm">
                          <p>{[contact.home_address.street1, contact.home_address.street2].filter(Boolean).join(', ')}</p>
                          <p>{contact.home_address.city}{contact.home_address.city && contact.home_address.state ? ', ' : ''}{contact.home_address.state} {contact.home_address.zip}</p>
                        </div>
                      ) : (
                        <p className="text-gray-400 italic">Not provided</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
                <PersonBusinesses personId={id} getAuthHeaders={getAuthHeaders} />
              </div>
            )}

            {!isPerson && (
            <div>
              <Card data-testid="business-information-card">
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <CardTitle className="flex items-center gap-2"><Building2 className="h-5 w-5" />Business Information</CardTitle>
                      <CardDescription>Business identity, contact details, and address</CardDescription>
                    </div>
                    {isAdmin && <Button variant="outline" size="sm" onClick={handleOpenEditDialog}><Edit className="h-4 w-4 mr-1" />Edit Business</Button>}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="space-y-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Identity</p>
                      {[
                        ['Legal Company Name', contact.company_name], ['DBA', contact.dba], ['Shortcode', contact.shortcode],
                        ['EIN', contact.ein], ['State of Incorporation', contact.state_of_incorporation],
                      ].map(([label, value]) => <div key={label}><p className="text-sm text-gray-500">{label}</p><p className={label === 'Shortcode' || label === 'EIN' ? 'font-mono' : ''}>{value || <span className="text-gray-400 italic">Not provided</span>}</p></div>)}
                      <div><p className="text-sm text-gray-500">Business Start Date</p><p>{contact.business_start_date?.split('T')[0] || <span className="text-gray-400 italic">Not provided</span>}{yearsInBusiness(contact.business_start_date) !== null && <span className="text-xs text-gray-400 ml-2" data-testid="derived-years-in-business">({yearsInBusiness(contact.business_start_date)} yrs in business)</span>}</p></div>
                    </div>
                    <div className="space-y-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Contact</p>
                      <div><p className="text-sm text-gray-500 flex items-center gap-1"><Phone className="h-3 w-3" />Business Phone</p><p>{formatPhoneDisplay(contact.phone || contact.phones?.[0]?.phone) || <span className="text-gray-400 italic">Not provided</span>}</p></div>
                      <div><p className="text-sm text-gray-500 flex items-center gap-1"><Mail className="h-3 w-3" />Business Email</p><p>{contact.email || contact.emails?.[0]?.email || <span className="text-gray-400 italic">Not provided</span>}</p></div>
                      {primaryContactName && <div className="rounded-md border border-amber-200 bg-amber-50 p-3"><p className="text-sm font-medium text-amber-900">Primary Contact</p><p>{primaryContactName}</p><p className="text-xs text-amber-700 mt-1">Legacy contact information; not a canonical owner or guarantor link.</p></div>}
                    </div>
                    <div className="space-y-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Business Address</p>
                      {(() => {
                        const fallback = contact.addresses?.find((a) => a.street1 || a.city) || {};
                        const address = { street1: contact.business_address || fallback.street1, street2: contact.business_street2 || fallback.street2, city: contact.business_city || fallback.city, state: contact.business_state || fallback.state, zip: contact.business_zip || fallback.zip };
                        if (!Object.values(address).some(Boolean)) return <p className="text-gray-400 italic">No business address provided.</p>;
                        return <div className="text-sm"><p>{address.street1}</p>{address.street2 && <p>{address.street2}</p>}<p>{address.city}{address.city && address.state ? ', ' : ''}{address.state} {address.zip}</p></div>;
                      })()}
                    </div>
                  </div>
                  {(!contact.company_name || !contact.shortcode || !(contact.phone || contact.phones?.[0]?.phone) || !(contact.email || contact.emails?.[0]?.email) || !(contact.business_address || contact.addresses?.some((a) => a.street1 || a.city))) && (
                    <div className="mt-6 rounded-lg border border-dashed bg-slate-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="incomplete-business-profile">
                      <div><p className="text-sm font-medium text-slate-700">This business profile is incomplete.</p><p className="text-xs text-slate-500">Add missing identity, contact, or address details to make it easier to use across deals.</p></div>
                      {isAdmin && <Button variant="outline" size="sm" onClick={handleOpenEditDialog}>Complete Profile</Button>}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            )}

            {/* Owners & Guarantors — canonical owners[] with legacy-flat fallback */}
            {!isPerson && (
              <OwnersSection
                contact={contact}
                isAdmin={isAdmin}
                getAuthHeaders={getAuthHeaders}
                onChanged={fetchContactProfile}
              />
            )}

            {/* Financial Information Card - Visible to all authenticated users for merchant contacts */}
            {(contact.is_merchant || isPerson) && (
              <Card className="mt-4 border-blue-200 bg-blue-50/30" data-testid="financial-info-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-blue-800">
                      <Shield className="h-5 w-5" />
                      {isPerson ? 'Sensitive Identity' : 'Sensitive & Banking Information'}
                      {isAdmin && <Badge className="bg-blue-100 text-blue-800 text-xs">Full Access</Badge>}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {/* SSN toggle only matters for admin (they can show/hide their full SSN view) */}
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowSensitive(!showSensitive)}
                          className="text-blue-700"
                          data-testid="toggle-sensitive-visibility"
                        >
                          {showSensitive ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                          {showSensitive ? 'Hide SSN' : 'Show SSN'}
                        </Button>
                      )}
                      {isAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleOpenSensitiveEdit}
                          className="border-blue-300 text-blue-800 hover:bg-blue-100"
                          data-testid="edit-sensitive-btn"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </div>
                  <CardDescription className="text-blue-700">
                    {isPerson
                      ? 'Identity information, encrypted at rest — masked by default'
                      : isAdmin
                        ? 'Bank account and SSN information (encrypted at rest) — masked by default'
                        : 'Bank account information for ACH payments — masked by default'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {sensitiveLoading ? (
                    <div className="text-center py-4">
                      <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* SSN Section - Admin sees full, non-admin sees masked last4 */}
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-500 flex items-center gap-2">
                          <Lock className="h-3 w-3" />
                          Social Security Number
                          {!isAdmin && <Badge variant="outline" className="text-xs">Last 4 only</Badge>}
                        </p>
                        {sensitiveData?.ssn_last4 || sensitiveData?.ssn_full ? (
                          <div className="space-y-2">
                            <p className="font-mono text-lg" data-testid="ssn-display">
                              {isAdmin && showSensitive && sensitiveData?.ssn_full
                                ? sensitiveData.ssn_full
                                : sensitiveData?.ssn_masked || `***-**-${sensitiveData?.ssn_last4 || '****'}`}
                            </p>
                            {sensitiveData?.legacy_ssn_migration_available && (
                              <div className="text-xs text-amber-700" data-testid="legacy-ssn-migration-notice">
                                <p>Existing SSN found in the legacy contact record. It is shown masked and has not yet been copied to protected storage.</p>
                                {isAdmin && (
                                  <Button type="button" variant="outline" size="sm" className="mt-2 border-amber-300" onClick={handleMigrateLegacySsn} disabled={migratingLegacySsn}>
                                    {migratingLegacySsn ? 'Moving…' : 'Move to protected storage'}
                                  </Button>
                                )}
                              </div>
                            )}
                            {sensitiveData?.legacy_ssn_conflict && (
                              <p className="text-xs text-red-700" data-testid="legacy-ssn-conflict">
                                Protected SSN ending in {sensitiveData.ssn_last4} differs from legacy SSN ending in {sensitiveData.legacy_ssn_last4}. The protected SSN was kept unchanged.
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-gray-400 italic">Not provided</p>
                        )}
                      </div>
                      
                      {/* Banking / ACH — business records only; masked by default */}
                      {!isPerson && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-gray-600">Banking / ACH</p>
                          {(sensitiveData?.bank_account_full || sensitiveData?.routing_number_full) && (
                            <Button variant="ghost" size="sm" className="h-6 text-blue-700" onClick={() => setShowBankFull(v => !v)} data-testid="toggle-bank-visibility">
                              {showBankFull ? <EyeOff className="h-3 w-3 mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
                              {showBankFull ? 'Hide' : 'Show'}
                            </Button>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Account Holder</p>
                          <p data-testid="account-holder-display">
                            {sensitiveData?.account_holder || <span className="text-gray-400 italic">Not provided</span>}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Bank Name</p>
                          <p data-testid="bank-name-display">
                            {sensitiveData?.bank_name || <span className="text-gray-400 italic">Not provided</span>}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Account Number</p>
                          <p className="font-mono" data-testid="account-number-display">
                            {sensitiveData?.bank_account_full
                              ? (showBankFull ? sensitiveData.bank_account_full : `••••${sensitiveData?.bank_account_last4 || String(sensitiveData.bank_account_full).slice(-4)}`)
                              : <span className="text-gray-400 italic">Not provided</span>}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Routing Number</p>
                          <p className="font-mono" data-testid="routing-number-display">
                            {sensitiveData?.routing_number_full
                              ? (showBankFull ? sensitiveData.routing_number_full : '•••••••••')
                              : <span className="text-gray-400 italic">Not provided</span>}
                          </p>
                        </div>
                        {sensitiveData?.acct_type && (
                          <div>
                            <p className="text-sm font-medium text-gray-500">Account Type</p>
                            <p className="capitalize" data-testid="acct-type-display">{sensitiveData.acct_type}</p>
                          </div>
                        )}
                      </div>
                      )}
                    </div>
                  )}
                  
                  {sensitiveData?.updated_at && (
                    <p className="text-xs text-gray-400 mt-4 border-t pt-2">
                      Last updated: {new Date(sensitiveData.updated_at).toLocaleString()} 
                      {sensitiveData.updated_by && ` by ${sensitiveData.updated_by}`}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Deals Tab */}
          <TabsContent value="deals">
            <Card>
              <CardContent className="p-0">
                {deals.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    No deals found for this contact.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-blue-50">
                          <TableHead>Deal ID</TableHead>
                          <TableHead>Deal Name</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead className="text-right">Advance</TableHead>
                          <TableHead className="text-right">Payback</TableHead>
                          <TableHead className="text-right">Paid</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                          <TableHead className="text-right">% Paid</TableHead>
                          <TableHead>Status</TableHead>
                          {isAdmin && <TableHead className="text-center">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {deals.map((deal) => {
                          const { role } = getDealRole(deal);
                          const paidPct = deal.payback > 0 ? ((deal.payments_made || 0) / deal.payback * 100) : 0;
                          
                          return (
                            <TableRow 
                              key={deal.id}
                              className="cursor-pointer hover:bg-gray-50"
                              onClick={() => navigate(`/deals/${deal.id}`)}
                              data-testid={`deal-row-${deal.deal_id}`}
                            >
                              <TableCell className="font-mono text-blue-600 font-medium">
                                {deal.deal_id}
                                {deal.deal_shortcode && (
                                  <span className="text-xs text-gray-400 ml-1.5">{deal.deal_shortcode}</span>
                                )}
                              </TableCell>
                              <TableCell className="font-medium">{deal.deal_name}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{role}</Badge>
                              </TableCell>
                              <TableCell className="text-right">{formatCurrency(deal.funding_amount)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(deal.payback)}</TableCell>
                              <TableCell className="text-right text-green-600">{formatCurrency(deal.payments_made)}</TableCell>
                              <TableCell className="text-right text-orange-600">{formatCurrency(deal.balance)}</TableCell>
                              <TableCell className="text-right">{paidPct.toFixed(1)}%</TableCell>
                              <TableCell>
                                <Badge className={getStatusBadgeColor(deal.status)}>
                                  {deal.status}
                                </Badge>
                              </TableCell>
                              {isAdmin && (
                                <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                    onClick={(e) => handleUnlinkClick(e, deal)}
                                    data-testid={`unlink-deal-${deal.deal_id}`}
                                  >
                                    <Unlink className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Syndications Tab */}
          {isSyndicator && syndications.length > 0 && (
            <TabsContent value="syndications">
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-indigo-50">
                          <TableHead>Deal ID</TableHead>
                          <TableHead>Deal Name</TableHead>
                          <TableHead className="text-right">Ownership %</TableHead>
                          <TableHead className="text-right">Invested</TableHead>
                          <TableHead className="text-right">Available</TableHead>
                          <TableHead className="text-right">Withdrawn</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {syndications.map((synd) => (
                          <TableRow 
                            key={synd.syndication_id}
                            className="cursor-pointer hover:bg-gray-50"
                            onClick={() => navigate(`/deals/${synd.deal_uuid}`)}
                            data-testid={`syndication-row-${synd.deal_id}`}
                          >
                            <TableCell className="font-mono text-blue-600 font-medium">{synd.deal_id}</TableCell>
                            <TableCell className="font-medium">{synd.deal_name}</TableCell>
                            <TableCell className="text-right">{synd.ownership_pct?.toFixed(2)}%</TableCell>
                            <TableCell className="text-right">{formatCurrency(synd.advance_amount)}</TableCell>
                            <TableCell className="text-right text-green-600">{formatCurrency(synd.available_total)}</TableCell>
                            <TableCell className="text-right text-gray-600">{formatCurrency(synd.withdrawn_total)}</TableCell>
                            <TableCell>
                              <Badge className={getStatusBadgeColor(synd.deal_status)}>
                                {synd.deal_status}
                              </Badge>
                              {synd.collect_fees && (
                                <Badge className="ml-1 bg-amber-100 text-amber-800">Fee Collector</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Capital Account Tab */}
          {isSyndicator && syndications.length > 0 && (
            <TabsContent value="capital-account">
              <CapitalAccountTab contactId={id} getAuthHeaders={getAuthHeaders} />
            </TabsContent>
          )}

          {/* Documents Tab */}
          <TabsContent value="documents" className="mt-4">
            <ContactDocumentsTab contactId={id} getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline" className="mt-4">
            <ContactTimelineTab contactId={id} getAuthHeaders={getAuthHeaders} />
          </TabsContent>
        </Tabs>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle>Edit Address Book Record</DialogTitle>
            </DialogHeader>
            <ContactForm
              formData={formData}
              setFormData={setFormData}
              onSubmit={handleSubmit}
              onCancel={() => setEditDialogOpen(false)}
              editingContact={contact}
              sensitiveData={sensitiveFormData}
              onSensitiveDataChange={setSensitiveFormData}
              getAuthHeaders={getAuthHeaders}
            />
          </DialogContent>
        </Dialog>

        {/* Unlink Confirmation Dialog */}
        <AlertDialog open={unlinkDialogOpen} onOpenChange={setUnlinkDialogOpen}>
          <AlertDialogContent data-testid="unlink-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Unlink Deal from Contact
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p>Are you sure you want to unlink this deal from the contact?</p>
                  {dealToUnlink && (
                    <div className="bg-gray-50 p-4 rounded-lg border space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Deal ID:</span>
                        <span className="font-mono font-medium">{dealToUnlink.deal_id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Deal Name:</span>
                        <span className="font-medium">{dealToUnlink.deal_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Current Contact:</span>
                        <span className="font-medium">{contact?.company_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Role:</span>
                        <span className="font-medium">{getDealRole(dealToUnlink).role}</span>
                      </div>
                    </div>
                  )}
                  <p className="text-sm text-amber-600">
                    This will make the deal orphaned. You can re-link it to the correct contact using &quot;Link Existing Deal&quot;.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={unlinking}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmUnlink}
                disabled={unlinking}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-unlink-btn"
              >
                {unlinking ? 'Unlinking...' : 'Unlink Deal'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Sensitive Data Edit Dialog */}
        <Dialog open={sensitiveEditDialogOpen} onOpenChange={setSensitiveEditDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="sensitive-edit-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-amber-600" />
                Edit Sensitive Information
              </DialogTitle>
              <DialogDescription>
                {isPerson ? 'Update identity details. All data is encrypted at rest.' : 'Update SSN and bank account details. All data is encrypted at rest.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSaveSensitiveData} className="space-y-4">
              {/* SSN */}
              <div className="space-y-2">
                <Label htmlFor="ssn_full">Social Security Number</Label>
                <Input
                  id="ssn_full"
                  value={sensitiveFormData.ssn_full}
                  onChange={(e) => setSensitiveFormData(prev => ({ ...prev, ssn_full: e.target.value }))}
                  placeholder="XXX-XX-XXXX"
                  data-testid="ssn-input"
                />
                <p className="text-xs text-gray-500">Leave empty to keep existing value</p>
              </div>
              
              {/* Banking fields — business records only */}
              {!isPerson && (
                <>
                  {/* Account Holder */}
                  <div className="space-y-2">
                    <Label htmlFor="account_holder">Account Holder</Label>
                    <Input
                      id="account_holder"
                      value={sensitiveFormData.account_holder}
                      onChange={(e) => setSensitiveFormData(prev => ({ ...prev, account_holder: e.target.value }))}
                      placeholder="Name on the account"
                      data-testid="account-holder-input"
                    />
                  </div>

                  {/* Bank Name */}
                  <div className="space-y-2">
                    <Label htmlFor="bank_name">Bank Name</Label>
                    <Input
                      id="bank_name"
                      value={sensitiveFormData.bank_name}
                      onChange={(e) => setSensitiveFormData(prev => ({ ...prev, bank_name: e.target.value }))}
                      placeholder="Enter bank name"
                      data-testid="bank-name-input"
                    />
                  </div>

                  {/* Account Number */}
                  <div className="space-y-2">
                    <Label htmlFor="bank_account_full">Account Number</Label>
                    <Input
                      id="bank_account_full"
                      value={sensitiveFormData.bank_account_full}
                      onChange={(e) => setSensitiveFormData(prev => ({ ...prev, bank_account_full: e.target.value }))}
                      placeholder="Enter account number"
                      data-testid="account-number-input"
                    />
                    <p className="text-xs text-gray-500">Leave empty to keep existing value</p>
                  </div>

                  {/* Routing Number */}
                  <div className="space-y-2">
                    <Label htmlFor="routing_number_full">Routing Number</Label>
                    <Input
                      id="routing_number_full"
                      value={sensitiveFormData.routing_number_full}
                      onChange={(e) => setSensitiveFormData(prev => ({ ...prev, routing_number_full: e.target.value }))}
                      placeholder="Enter routing number"
                      data-testid="routing-number-input"
                    />
                    <p className="text-xs text-gray-500">Leave empty to keep existing value</p>
                  </div>

                  {/* Account Type */}
                  <div className="space-y-2">
                    <Label htmlFor="acct_type">Account Type</Label>
                    <Select
                      value={sensitiveFormData.acct_type}
                      onValueChange={(value) => setSensitiveFormData(prev => ({ ...prev, acct_type: value }))}
                    >
                      <SelectTrigger data-testid="acct-type-select">
                        <SelectValue placeholder="Select account type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="checking">Checking</SelectItem>
                        <SelectItem value="savings">Savings</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

                            <DialogFooter className="pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setSensitiveEditDialogOpen(false)}
                  disabled={savingSensitive}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={savingSensitive}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="save-sensitive-btn"
                >
                  {savingSensitive ? (
                    <>Saving...</>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ADD ANOTHER COMPANY (Josh 08/10) */}
        <Dialog open={!!addCoDlg} onOpenChange={(open) => !open && setAddCoDlg(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Another Company</DialogTitle>
              <DialogDescription>
                Creates a new contact for a second business owned by{' '}
                {contact?.name || contact?.company_name} — carrying over their details,
                with its own shortcode, linked back to this profile.
              </DialogDescription>
            </DialogHeader>
            {addCoDlg && (
              <div className="space-y-3">
                <div>
                  <Label>Company Name *</Label>
                  <Input
                    value={addCoDlg.name}
                    onChange={(e) => setAddCoDlg((d) => ({ ...d, name: e.target.value }))}
                    placeholder="SECOND BUSINESS LLC"
                    data-testid="add-company-name"
                  />
                </div>
                <div>
                  <Label>Shortcode * (unique)</Label>
                  <Input
                    value={addCoDlg.shortcode}
                    onChange={(e) => setAddCoDlg((d) => ({ ...d, shortcode: e.target.value.toUpperCase() }))}
                    placeholder="e.g. SBZ"
                    className="font-mono"
                    data-testid="add-company-shortcode"
                  />
                </div>
                <div>
                  <Label>DBA (optional)</Label>
                  <Input
                    value={addCoDlg.dba}
                    onChange={(e) => setAddCoDlg((d) => ({ ...d, dba: e.target.value }))}
                    data-testid="add-company-dba"
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAddCoDlg(null)}>Cancel</Button>
                  <Button onClick={submitAddCompany} disabled={addCoDlg.saving} data-testid="add-company-submit">
                    {addCoDlg.saving ? 'Creating…' : 'Create & Link'}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <SendForSignatureDialog
          open={signportDialogOpen}
          onOpenChange={setSignportDialogOpen}
          contact={contact}
          getAuthHeaders={getAuthHeaders}
        />
      </div>
    </Layout>
  );
};

export default ContactProfile;

// ─── Capital Account Tab (dynamic, no stored totals) ─────────────────────────
const CapitalAccountTab = ({ contactId, getAuthHeaders }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('receivable');
  const [sortDir, setSortDir] = useState('desc');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/contacts/${contactId}/capital-account`,
          { headers: getAuthHeaders() }
        );
        setData(res.data);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [contactId, getAuthHeaders]);

  if (loading) return <div className="animate-pulse h-48 bg-gray-100 rounded-xl" />;
  if (!data) return <p className="text-sm text-gray-400 py-8 text-center">Failed to load capital account</p>;

  const { summary, deals } = data;

  const sorted = [...deals].sort((a, b) => {
    const aVal = a[sortKey] ?? 0;
    const bVal = b[sortKey] ?? 0;
    return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
  });

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sortIcon = (key) => sortKey === key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : '';

  return (
    <div className="space-y-4" data-testid="capital-account-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-gray-500">Total Funded</p>
          <p className="text-lg font-bold" data-testid="cap-total-funded">{formatCurrency(summary.total_funded)}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-gray-500">Principal Returned</p>
          <p className="text-lg font-bold text-green-600" data-testid="cap-principal-returned">{formatCurrency(summary.total_principal_returned)}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-gray-500">Fees Collected</p>
          <p className="text-lg font-bold text-amber-600" data-testid="cap-fees-collected">{formatCurrency(summary.total_fees_collected)}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-gray-500">Net Receivable</p>
          <p className={`text-lg font-bold ${summary.net_receivable > 0 ? 'text-red-600' : 'text-green-600'}`} data-testid="cap-net-receivable">
            {formatCurrency(summary.net_receivable)}
          </p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-gray-500">Active Deals</p>
          <p className="text-lg font-bold" data-testid="cap-active-deals">{summary.active_deal_count}</p>
        </CardContent></Card>
      </div>

      {/* Deal Breakdown Table */}
      <Card>
        <CardContent className="p-0">
          {deals.length === 0 ? (
            <p className="text-sm text-gray-400 py-8 text-center">No syndicated deals</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Deal</TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('ownership_pct')}>
                      Ownership %{sortIcon('ownership_pct')}
                    </TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort('funded_amount')}>
                      Funded{sortIcon('funded_amount')}
                    </TableHead>
                    <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort('principal_returned')}>
                      Principal Returned{sortIcon('principal_returned')}
                    </TableHead>
                    <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort('fees_collected')}>
                      Fees Collected{sortIcon('fees_collected')}
                    </TableHead>
                    <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort('receivable')}>
                      Net Receivable{sortIcon('receivable')}
                    </TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((d) => (
                    <TableRow
                      key={d.deal_uuid}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => navigate(`/deals/${d.deal_uuid}`)}
                      data-testid={`cap-deal-row-${d.deal_id}`}
                    >
                      <TableCell>
                        <span className="font-mono text-xs text-blue-600">#{d.deal_id}</span>
                        <span className="ml-2 text-sm">{d.deal_name}</span>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{d.ownership_pct}%</TableCell>
                      <TableCell>
                        <Badge className={d.role === 'PARTNER' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-700'}>
                          {d.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(d.funded_amount)}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-green-600">{formatCurrency(d.principal_returned)}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-amber-600">
                        {d.collect_fees ? formatCurrency(d.fees_collected) : <span className="text-gray-300">—</span>}
                      </TableCell>
                      <TableCell className={`text-right font-mono text-sm font-bold ${d.receivable > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(d.receivable)}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadgeColor(d.deal_status)}>{d.deal_status}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
